import { useEffect, useRef } from 'react';
import ErrorPage403 from '@/app/routes/errorPage403';
import { useUserPrivileges } from '@shared/hooks/useUserPrivileges';
import { TextField } from '@/shared/components/TextField';
import { ProfilePictureUpload } from '@/shared/components/ProfilePictureUpload';
import FileUpload from '@/shared/components/FileUpload';
import { FileType } from '@/shared/components/FileUpload/constants';
import CreateLayout from '@/shared/layout/createLayout';
import { Loader } from '@/shared/components/Loader';
import { ToggleButton } from '@/shared/components/ToggleButton';
import { Snackbar } from '@/shared/components/Snackbar';
import { UpdateStatusDialog } from '@/epics/adminManagment/pages/adminList/UpdateStatusDialog';
import { getTechnicianStatusDialogConfig } from '../technicianList/technicianStatusDialogConfig';
import { Tooltip } from '@/shared/components/Tooltip';
import { TECHNICIAN_LABELS } from '../../constants';
import { TECHNICIAN_ERROR_MESSAGES } from '../../errors/errorMessages';
import { useEditTechnician } from '../../hooks/useEditTechnician';
import { translateRole } from '@/shared/utils/roleTranslation';
import PrivilegesSection from '../addTechnician/PrivilegesSection';

const EditTechnician = () => {
  const { privileges: callerPrivileges } = useUserPrivileges();
  const {
    formData,
    errors,
    submitErrorTrigger,
    isSubmitting,
    isLoading,
    snackbarError,
    isSnackbarOpen,
    handleCloseSnackbar,
    isCancelModalOpen,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleNameChange,
    handlePhoneChange,
    handleAddress1Change,
    handleAddress2Change,
    handleCityChange,
    handleStateChange,
    handleZipChange,
    handleProfileFileChange,
    handleProfileFileDelete,
    handleProfileFileError,
    handleDocFilesChange,
    handleExistingDocDelete,
    handleDocFilesError,
    handlePrivilegesChange,
    accessError,
    handleSubmit,
    handleCancel,
    profileFile,
    existingProfileUrl,
    docFiles,
    existingDocs,
    status,
    handleStatusChange,
    isStatusDialogOpen,
    handleCloseStatusDialog,
    handleConfirmStatusUpdate,
    isStatusSubmitting,
    showStatusSuccessSnackbar,
    statusSuccessMessage,
    handleCloseStatusSuccessSnackbar,
    showStatusErrorSnackbar,
    statusErrorMessage,
    handleCloseStatusErrorSnackbar,
  } = useEditTechnician();

  const errorsRef = useRef(errors);
  errorsRef.current = errors;

  useEffect(() => {
    if (submitErrorTrigger === 0) {
      return;
    }
    const fieldOrder: Array<[string, string]> = [
      ['name', 'edit-technician-name'],
      ['phone', 'edit-technician-phone'],
      ['address1', 'edit-technician-address1'],
      ['city', 'edit-technician-city'],
      ['state', 'edit-technician-state'],
      ['zip', 'edit-technician-zip'],
    ];
    const firstError = fieldOrder.find(([key]) =>
      Boolean(errorsRef.current[key])
    );
    if (firstError) {
      document
        .querySelector(`[data-testid="${firstError[1]}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [submitErrorTrigger]);

  const EditTechnicianHeader = () => (
    <div className="flex items-center justify-between">
      <p className="text-titleLarge text-neutral-10">
        {TECHNICIAN_LABELS.EDIT_PAGE_TITLE}
      </p>
      <div
        className={`flex items-center gap-2${!callerPrivileges.technicianActivateDeactivate ? ' opacity-50' : ''}`}
      >
        <p className="text-[12px] text-on-surface-variant">Status:</p>
        {!callerPrivileges.technicianActivateDeactivate ? (
          <Tooltip
            content={TECHNICIAN_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP}
            side="left"
            forceShow
            cursor="not-allowed"
          >
            <div className="flex min-w-[110px] items-center">
              <div style={{ pointerEvents: 'none' }} className="leading-none">
                <ToggleButton
                  checked={status === 'active'}
                  onChange={() => {}}
                  label={
                    status === 'active'
                      ? TECHNICIAN_LABELS.STATUS_ACTIVE
                      : TECHNICIAN_LABELS.STATUS_INACTIVE
                  }
                  size="XSmall"
                  disabled={true}
                  data-testid="toggle-status"
                />
              </div>
            </div>
          </Tooltip>
        ) : (
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleStatusChange();
            }}
            className="cursor-pointer flex min-w-[110px] items-center"
          >
            <div style={{ pointerEvents: 'none' }} className="leading-none">
              <ToggleButton
                checked={status === 'active'}
                onChange={() => {}}
                label={
                  status === 'active'
                    ? TECHNICIAN_LABELS.STATUS_ACTIVE
                    : TECHNICIAN_LABELS.STATUS_INACTIVE
                }
                size="XSmall"
                disabled={isSubmitting || isLoading || isStatusSubmitting}
                data-testid="toggle-status"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const getInitials = (name: string) => {
    if (!name) {
      return '?';
    }
    const parts = name.trim().split(' ');
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name[0]?.toUpperCase() || '?';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center bg-white h-screen w-full">
        <Loader text="Ładowanie danych Edycja Technika" />
      </div>
    );
  }

  if (accessError) {
    return <ErrorPage403 />;
  }

  return (
    <>
      <CreateLayout
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        isCancelModalOpen={isCancelModalOpen}
        handleCloseCancelModal={handleCloseCancelModal}
        handleConfirmCancel={handleConfirmCancel}
        isSnackbarOpen={isSnackbarOpen}
        handleCloseSnackbar={handleCloseSnackbar}
        snackbarError={snackbarError}
        Header={EditTechnicianHeader}
        submitButtonLabel={TECHNICIAN_LABELS.EDIT_SUBMIT_BUTTON}
        submitButtonLoadingLabel={TECHNICIAN_LABELS.EDIT_SUBMIT_BUTTON_LOADING}
      >
        <div className="flex flex-col gap-8">
          {/* Basic Info */}
          <div className="flex flex-col gap-2">
            <p className="text-[18px] text-on-surface-variant">
              {TECHNICIAN_LABELS.SECTION_BASIC_INFO}
            </p>

            <ProfilePictureUpload
              onFileChange={handleProfileFileChange}
              onDelete={handleProfileFileDelete}
              onError={handleProfileFileError}
              disabled={isSubmitting}
              profileFile={profileFile}
              existingImageUrl={existingProfileUrl}
              initialLetter={getInitials(formData.name)}
            />

            <div className="flex flex-col gap-8">
              <div className="flex gap-4">
                <TextField
                  fullWidth={true}
                  label={TECHNICIAN_LABELS.FIELD_NAME}
                  dataTestId="edit-technician-name"
                  autoTrimMaxLength={200}
                  value={formData.name}
                  onChange={handleNameChange}
                  error={Boolean(errors['name'])}
                  errorMessage={errors['name'] || ''}
                  disabled={isSubmitting}
                  autoFocus={true}
                />
                <TextField
                  fullWidth={true}
                  label={TECHNICIAN_LABELS.FIELD_EMAIL}
                  dataTestId="edit-technician-email"
                  autoTrimMaxLength={100}
                  value={formData.email}
                  disabled={true}
                />
                <TextField
                  fullWidth={true}
                  label={TECHNICIAN_LABELS.FIELD_PHONE}
                  dataTestId="edit-technician-phone"
                  type="tel"
                  maxLength={9}
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  error={Boolean(errors['phone'])}
                  errorMessage={errors['phone'] || ''}
                  disabled={isSubmitting}
                />
              </div>

              <div className="flex gap-4 w-[32%]">
                <TextField
                  fullWidth={true}
                  label={TECHNICIAN_LABELS.FIELD_TECHNICIAN_ID}
                  dataTestId="edit-technician-id"
                  value={formData.technician_id}
                  disabled={true}
                />
              </div>
            </div>
          </div>

          <div className="border-b-2 border-[#DCDDE0]" />

          {/* Address */}
          <div className="flex flex-col gap-2">
            <p className="text-[18px] text-on-surface-variant">
              {TECHNICIAN_LABELS.SECTION_ADDRESS}
            </p>
            <div className="flex flex-col gap-8">
              <div className="flex gap-4">
                <TextField
                  fullWidth={true}
                  label={TECHNICIAN_LABELS.FIELD_ADDRESS1}
                  dataTestId="edit-technician-address1"
                  autoTrimMaxLength={200}
                  value={formData.address1}
                  onChange={handleAddress1Change}
                  error={Boolean(errors['address1'])}
                  errorMessage={errors['address1'] || ''}
                  disabled={isSubmitting}
                />
                <TextField
                  fullWidth={true}
                  label={
                    <>
                      {TECHNICIAN_LABELS.FIELD_ADDRESS2}
                      <span className="text-bodySmall font-normal">
                        {' '}
                        {TECHNICIAN_LABELS.FIELD_OPTIONAL}
                      </span>
                    </>
                  }
                  dataTestId="edit-technician-address2"
                  autoTrimMaxLength={200}
                  value={formData.address2}
                  onChange={handleAddress2Change}
                  disabled={isSubmitting}
                />
                <TextField
                  fullWidth={true}
                  label={TECHNICIAN_LABELS.FIELD_CITY}
                  dataTestId="edit-technician-city"
                  autoTrimMaxLength={200}
                  value={formData.city}
                  onChange={handleCityChange}
                  error={Boolean(errors['city'])}
                  errorMessage={errors['city'] || ''}
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex gap-4">
                <TextField
                  fullWidth={true}
                  label={TECHNICIAN_LABELS.FIELD_STATE}
                  dataTestId="edit-technician-state"
                  autoTrimMaxLength={200}
                  value={formData.state}
                  onChange={handleStateChange}
                  error={Boolean(errors['state'])}
                  errorMessage={errors['state'] || ''}
                  disabled={isSubmitting}
                />
                <TextField
                  fullWidth={true}
                  label={
                    <>
                      {TECHNICIAN_LABELS.FIELD_ZIP}
                      <span className="text-bodySmall font-normal">
                        {' '}
                        {TECHNICIAN_LABELS.FIELD_OPTIONAL}
                      </span>
                    </>
                  }
                  dataTestId="edit-technician-zip"
                  maxLength={200}
                  value={formData.zip}
                  onChange={handleZipChange}
                  error={Boolean(errors['zip'])}
                  errorMessage={errors['zip'] || ''}
                  disabled={isSubmitting}
                />
                <TextField
                  fullWidth={true}
                  label={TECHNICIAN_LABELS.FIELD_COUNTRY}
                  dataTestId="edit-technician-country"
                  maxLength={200}
                  value={formData.country}
                  disabled={true}
                />
              </div>
            </div>
          </div>

          <div className="border-b-2 border-[#DCDDE0]" />

          {/* Tax & Compliance Details */}
          <div className="flex flex-col gap-2">
            <p className="text-[18px] text-on-surface-variant">
              {TECHNICIAN_LABELS.SECTION_TAX_COMPLIANCE}
            </p>
            <div className="flex flex-col gap-2">
              <p className="text-[#474A4C]">
                <span className="text-[16px] leading-[24px] tracking-[0.5px] font-normal">
                  {TECHNICIAN_LABELS.FIELD_GOVERNMENT_ID}
                </span>
                <span className="text-[12px] leading-[16px] tracking-[0.4px] font-normal">
                  {' '}
                  {TECHNICIAN_LABELS.FIELD_GOVERNMENT_ID_OPTIONAL}
                </span>
              </p>
            </div>

            <FileUpload
              onFileSelect={handleDocFilesChange}
              onError={handleDocFilesError}
              selectedFiles={docFiles}
              existingFiles={existingDocs}
              onExistingFileDelete={handleExistingDocDelete}
              allowedTypes={[
                FileType.PNG,
                FileType.JPG,
                FileType.JPEG,
                FileType.PDF,
              ]}
              maxFiles={2}
              disabled={isSubmitting}
            />
          </div>

          <div className="border-b-2 border-[#DCDDE0]" />

          {/* Role & Permissions */}
          <div className="flex flex-col gap-4">
            <p className="text-[18px] text-on-surface-variant">
              {TECHNICIAN_LABELS.SECTION_ACCESS}
            </p>
            <div className="flex gap-4 w-[32%]">
              <TextField
                fullWidth={true}
                label={TECHNICIAN_LABELS.FIELD_ROLE}
                dataTestId="edit-technician-role"
                value={translateRole(formData.role)}
                disabled={true}
              />
            </div>
            <PrivilegesSection
              privileges={formData.privileges}
              onPrivilegesChange={handlePrivilegesChange}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </CreateLayout>

      {/* Status Update Dialog */}
      <UpdateStatusDialog
        open={isStatusDialogOpen}
        onClose={handleCloseStatusDialog}
        onConfirm={handleConfirmStatusUpdate}
        isSubmitting={isStatusSubmitting}
        {...getTechnicianStatusDialogConfig(formData.name, status === 'active')}
      />

      {/* Status Update Success Snackbar */}
      <Snackbar
        open={showStatusSuccessSnackbar}
        message={statusSuccessMessage}
        variant="success"
        onClose={handleCloseStatusSuccessSnackbar}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton={true}
      />

      {/* Status Update Error Snackbar */}
      <Snackbar
        open={showStatusErrorSnackbar}
        message={statusErrorMessage}
        variant="error"
        onClose={handleCloseStatusErrorSnackbar}
        autoHideDuration={5000}
        position="top-center"
        showCloseButton={true}
      />
    </>
  );
};

export default EditTechnician;
